import type { CellViewWire, IntelReportWire, SeatIdentity } from '@badger-sentinel/protocol';
import { useEffect } from 'react';

import { useConsoleStore, type ViewerRef } from './store.js';

/**
 * The viewer guard (M5-D9, shell.md must-not #2).
 *
 * > Never render a snapshot whose `view.viewer` doesn't match the current seat
 * > identity. After a seat switch, a late snapshot for the previous seat is a
 * > cross-cell leak. **Assert it.**
 *
 * The projection is the security boundary and it stamps every view with the
 * seat it was built for. That stamp is worth nothing if the client renders
 * whatever arrives, and the case is not hypothetical: a seat switch, a socket
 * that outlived its token, and a frame replayed by `resume` all put a view for
 * one seat on a socket now showing another.
 *
 * The rule is to **drop**, not to reconcile. A view that is not this seat's
 * cannot be repaired into one, and merging it would be exactly the manufactured
 * knowledge must-not #1 forbids.
 */

/**
 * Does this document belong to this seat?
 *
 * Widened from `CellViewWire` to *anything the projection stamps a viewer on*,
 * which as of Phase 8 is two things: the snapshot, and the intel report. Both
 * carry `zViewerRef` and both are re-projected per observer, so one predicate
 * answers for both — and the archive, which fetches a report over REST rather
 * than receiving one on the socket, is held to the same rule by construction
 * rather than by somebody remembering to write it again.
 */
export function viewerMatches(
  subject: { readonly viewer: ViewerRef },
  seat: ViewerRef | null,
): boolean {
  if (seat === null) return false;
  return subject.viewer.team === seat.team && subject.viewer.cell === seat.cell;
}

export function seatRefOf(seat: SeatIdentity | null): ViewerRef | null {
  return seat === null ? null : { team: seat.team, cell: seat.cell };
}

/**
 * The view to render, or null.
 *
 * Null has two very different causes and the caller must not conflate them: no
 * snapshot has arrived yet (normal, on every attach), and a snapshot arrived
 * for somebody else (a bug, and counted).
 */
export function guardedView(
  view: CellViewWire | null,
  seat: ViewerRef | null,
): CellViewWire | null {
  if (view === null || seat === null) return null;
  return viewerMatches(view, seat) ? view : null;
}

/**
 * The app-side binding: drops the view, counts it, and says so out loud.
 *
 * The count is recorded from an effect rather than during render. Dropping is
 * a *render-time* decision — nothing else would be correct, since the wrong
 * view must not reach a child — but a store write during render re-enters the
 * same render, and a view that stays mismatched would spin.
 */
export function useGuardedView(view: CellViewWire | null): CellViewWire | null {
  const seat = useConsoleStore((state) => state.seat);
  const noteDroppedSnapshot = useConsoleStore((state) => state.noteDroppedSnapshot);
  const dropped = view !== null && seat !== null && !viewerMatches(view, seat);

  useEffect(() => {
    if (!dropped) return;
    noteDroppedSnapshot();
    // The one place this file logs. A dropped snapshot is silent by design —
    // nothing renders, so nothing looks wrong — and a leak that is both real
    // and invisible is the worse of the two ways this can fail.
    // eslint-disable-next-line no-console
    console.error('[console] dropped a snapshot addressed to another seat (shell.md must-not #2)');
  }, [dropped, view, noteDroppedSnapshot]);

  return guardedView(view, seat);
}

/**
 * The intel report to render, or null (M5-D9, shell.md must-not #2).
 *
 * The same rule as `guardedView`, applied to the other document the projection
 * stamps: a report addressed to another seat is **dropped**, never reconciled.
 * It matters more here than it looks, because a report reaches this console by
 * two routes — the socket's `intel.report`, and the archive's REST pull — and the
 * second is a request the client itself makes with a token. A stale token, a seat
 * switch mid-flight, or a response arriving after the operator has moved seats
 * all put one seat's aliases in front of another, and the aliases are the whole
 * point.
 */
export function guardedReport(
  report: IntelReportWire | null,
  seat: ViewerRef | null,
): IntelReportWire | null {
  if (report === null || seat === null) return null;
  return viewerMatches(report, seat) ? report : null;
}
